import React from "react";

import { useStrings } from "../../localization/localizationHelpers";
import * as styles from "./ReadOnlyFieldList.module.css";

type YesNoLabels = Readonly<{ yes: string; no: string }>;

type FieldConfig = Readonly<{
  key: string;
  label: string;
  /** For object arrays — sub-field labels keyed by field name */
  subFields?: ReadonlyArray<
    Readonly<{
      key: string;
      label: string;
      /** Map raw values to display strings */
      valueMap?: Readonly<Record<string, string>>;
    }>
  >;
  /** For object arrays — filter which items to display */
  filter?: (item: Record<string, unknown>) => boolean;
}>;

type ReadOnlyFieldListProps = Readonly<{
  data: Record<string, unknown> | undefined;
  fields: ReadonlyArray<FieldConfig>;
}>;

const formatValue = (value: unknown, labels: YesNoLabels): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  if (typeof value === "boolean") {
    return value ? labels.yes : labels.no;
  }
  if (typeof value === "number") {
    return String(value);
  }
  return String(value);
};

const renderField = (field: FieldConfig, value: unknown, labels: YesNoLabels) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  // Array of strings (DynamicList fields)
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === "string") {
    const filtered = value.filter((v: string) => v?.trim());
    if (filtered.length === 0) {
      return null;
    }
    return (
      <div key={field.key} className={styles.readOnlyField}>
        <span className={styles.readOnlyLabel}>{field.label}</span>
        <div className={styles.readOnlyList}>
          {filtered.map((item: string, i: number) => (
            <span key={i} className={styles.readOnlyListItem}>
              {item}
            </span>
          ))}
        </div>
      </div>
    );
  }

  // Array of objects (DynamicObjectList fields)
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object") {
    const items = field.filter
      ? value.filter((obj: Record<string, unknown>) => field.filter!(obj))
      : value;
    if (items.length === 0) {
      return null;
    }
    return (
      <div key={field.key} className={styles.readOnlyField}>
        <span className={styles.readOnlyLabel}>{field.label}</span>
        <div className={styles.readOnlyObjectList}>
          {items.map((obj: Record<string, unknown>, i: number) => (
            <div key={i} className={styles.readOnlyObjectItem}>
              {(field.subFields ?? Object.keys(obj).map(k => ({ key: k, label: k }))).map(sf => {
                const raw = formatValue(obj[sf.key], labels);
                if (!raw) {
                  return null;
                }
                const display = "valueMap" in sf && sf.valueMap ? sf.valueMap[raw] ?? raw : raw;
                return (
                  <div key={sf.key} className={styles.readOnlyField}>
                    <span className={styles.readOnlyLabel}>{sf.label}</span>
                    <span className={styles.readOnlyValue}>{display}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Scalar values
  const formatted = formatValue(value, labels);
  if (!formatted) {
    return null;
  }

  return (
    <div key={field.key} className={styles.readOnlyField}>
      <span className={styles.readOnlyLabel}>{field.label}</span>
      <span className={styles.readOnlyValue}>{formatted}</span>
    </div>
  );
};

export const ReadOnlyFieldList = ({ data, fields }: ReadOnlyFieldListProps) => {
  const [yes, no, noDataAvailable, noDataSubmitted] = useStrings(
    "yes",
    "no",
    "noDataAvailable",
    "noDataSubmittedForThisSection"
  );
  const labels: YesNoLabels = { yes, no };
  if (!data) {
    return <p className={styles.emptyMessage}>{noDataAvailable}</p>;
  }

  const renderedFields = fields
    .map(field => renderField(field, data[field.key], labels))
    .filter(Boolean);

  if (renderedFields.length === 0) {
    return <p className={styles.emptyMessage}>{noDataSubmitted}</p>;
  }

  return <div className={styles.readOnlyContent}>{renderedFields}</div>;
};
